/**
 * Feature Flags Configuration
 *
 * PURPOSE: Safe rollback mechanism for UI/UX changes
 *
 * HOW TO USE:
 * 1. Import: import { FEATURE_FLAGS } from '../config/featureFlags';
 * 2. Check: if (FEATURE_FLAGS.PREMIUM_TYPOGRAPHY) { ... }
 * 3. Rollback: Set flag to false to revert to old behavior
 *
 * ROLLBACK PROCEDURE:
 * - If any feature causes issues, set its flag to `false`
 * - No code changes needed, just toggle the flag
 * - Rebuild and deploy
 *
 * @version 1.0.0
 * @date December 2025
 */

export const FEATURE_FLAGS = {
  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 1: CHAT EXPERIENCE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Premium Typography
   * - Larger fonts (15px → 17px body text)
   * - Brighter colors (#E2E8F0 → #F1F5F9)
   * - Larger Q avatar (22px → 26px)
   *
   * Files affected: QChatMessages.js
   * Rollback: Set to false to use original smaller fonts
   */
  PREMIUM_TYPOGRAPHY: true,

  /**
   * Expandable Chat Input
   * - Input grows upward as user types
   * - Max 6 lines on desktop, 4 on mobile
   * - New placeholder: "Ask Q..."
   *
   * Files affected: QChatInput.js
   * Rollback: Set to false to use fixed-height input
   */
  EXPANDABLE_INPUT: true,

  /**
   * Streamlined Action Chips
   * - Reduce from 6 buttons to 3 (Signal, Analyze, Coach)
   * - No token costs displayed on buttons
   * - Cleaner single-row layout
   *
   * Files affected: QActionChips.js, useQChat.js (welcome message)
   * Rollback: Set to false to show all 6 buttons
   */
  STREAMLINED_ACTIONS: true,

  /**
   * Premium Welcome Message
   * - Centered card layout
   * - Personalized greeting with name
   * - 3 action buttons only
   * - Context line (signals/trades count)
   *
   * Files affected: useQChat.js (createWelcomeMessage)
   * Rollback: Set to false to use original welcome message
   */
  PREMIUM_WELCOME: true,

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 2: LAYOUT & NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Header Token Display
   * - Token balance in header with +Add button
   * - Low balance warning banner
   *
   * Files affected: QChatWindow.js, QAICoachPage.js
   * Rollback: Set to false to hide header token display
   */
  HEADER_TOKEN_DISPLAY: true,

  /**
   * Collapsible Desktop Dashboard
   * - Dashboard can be collapsed to give more chat space
   * - Toggle button to expand/collapse
   *
   * Files affected: QAICoachPage.js
   * Rollback: Set to false to use fixed dashboard
   */
  COLLAPSIBLE_DASHBOARD: true,

  /**
   * Mobile Responsive Layout
   * - 100vh full-screen chat
   * - FAB button to toggle dashboard
   * - Touch-optimized (48px targets)
   *
   * Files affected: QAICoachPage.js
   * Rollback: Set to false to use original mobile layout
   */
  MOBILE_RESPONSIVE: true,

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 3: DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Simplified Dashboard
   * - Simple text table (Investing.com style)
   * - Pivot, R1, S1 columns (placeholder)
   * - Technicals section (Fib, MA placeholder)
   * - Remove Beta, Spread columns
   *
   * Files affected: QSignalTable.js, QSignalBoard.js
   * Rollback: Set to false to use original dashboard
   */
  SIMPLIFIED_DASHBOARD: true,

  /**
   * MT5-Style Grid View (Phase 1 Polish)
   * - Grid/Table toggle in Q Signal Board header
   * - QSignalGridCard cards with expand/collapse
   * - Category filtering (Forex, Metals, Crypto)
   *
   * Files affected: QSignalBoard.js, QAICoachPage.js
   * Dependencies: QSignalGridCard.js, QSignalGrid.js
   * Rollback: Set to false to hide grid toggle
   */
  SIGNAL_GRID_VIEW: true,

  /**
   * Enhanced Signal Grid (Phase 1B - Rich Data)
   * - Symbol selector dropdown in Signal Board header
   * - Full QuantX data for selected symbol (ADX, ATR, Fib, DNA)
   * - Momentum & Volatility sections in grid cards
   * - Strategy DNA section (Bounce/Breakout/Fakeout)
   *
   * Files affected: QSignalBoard.js, QSignalGridCard.js, QAICoachPage.js
   * Dependencies: useQuantXData hook, /api/quantx/* endpoints
   * Rollback: Set to false to hide enhanced data sections
   */
  ENHANCED_SIGNAL_GRID: true,

  // ═══════════════════════════════════════════════════════════════════════════
  // FUTURE FEATURES (Disabled by default)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * File Attachments UI
   * - Attachment icon in chat input
   * - File picker and preview
   * - NOTE: Backend not ready, UI only
   *
   * Files affected: QChatInput.js
   * Enable: Set to true when backend supports attachments
   */
  CHAT_ATTACHMENTS_UI: false,

  /**
   * Paste Screenshot Support
   * - Ctrl+V to paste images
   * - Preview before send
   * - NOTE: Backend not ready
   *
   * Files affected: QChatInput.js
   * Enable: Set to true when backend supports image processing
   */
  PASTE_SCREENSHOT: false,

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 5: EA ASSISTANT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * EA Assistant Tab
   * - My EAs tab in Q.AI Coach page
   * - List MQL5 files from MT5
   * - Analyze and modify EAs with Q.AI
   * - Monaco diff view for changes
   *
   * Files affected: QAICoachPage.js, EAManagerTab.js, EAFileList.js
   * Dependencies: QBridge running on localhost:5556
   */
  EA_ASSISTANT_TAB: true,
};

/**
 * Helper function to check if a feature is enabled
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} - Whether the feature is enabled
 */
export const isFeatureEnabled = (flagName) => {
  return FEATURE_FLAGS[flagName] === true;
};

/**
 * Get all enabled features (for debugging)
 * @returns {string[]} - Array of enabled feature names
 */
export const getEnabledFeatures = () => {
  return Object.entries(FEATURE_FLAGS)
    .filter(([_, enabled]) => enabled)
    .map(([name]) => name);
};

export default FEATURE_FLAGS;
